import { useCallback, useState, useEffect, memo } from 'react'
// @mui
import {
  Typography,
  Stack,
  Box,
  FormHelperText,
  Dialog,
} from '@mui/material'
import { SxProps, styled } from '@mui/material/styles'
// components
import Iconify from 'components/Iconify'
import { RHFUploadOutlined } from 'src/components/hook-form'
import ProgressBar from '../ProgressBar'
// helpers
import { formatBytes } from 'helpers/formats/string'
import useLocales from 'hooks/useLocales'

const DropZoneStyle = styled('div')(({ theme }) => ({
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  outline: 'none',
  overflow: 'hidden',
  position: 'relative',
  marginBottom: '16px',
  borderRadius: theme.shape.borderRadius,
  transition: theme.transitions.create('padding'),
  border: `1px dashed ${theme.palette.grey[500_32]}`,
}))

const DropZoneIdCard = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  outline: 'none',
  overflow: 'hidden',
  position: 'relative',
  marginBottom: '16px',
  borderRadius: theme.shape.borderRadius,
  transition: theme.transitions.create('padding'),
  border: `1px dotted ${theme.palette.grey[500_32]}`,
  padding: '4px',
  img: {
    height: '163px',
    [theme.breakpoints.up('sm')]: {
      width: '304px',
    },
  },
}))

const DropZoneEventCard = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  outline: 'none',
  overflow: 'hidden',
  borderRadius: theme.shape.borderRadius,
  transition: theme.transitions.create('padding'),
  border: `1px dashed ${theme.palette.grey[500_32]}`,
  padding: '16px 24px',
}))

const DropZoneEventSmallPreview = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '60px',
  width: '60px',
  cursor: 'pointer',
  'img, video': {
    height: '100% !important',
    width: '100% !important',
    objectFit: 'cover',
    aspectRatio: '1 / 1',
    borderRadius: '8px',
  },
  '.overlay': {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    position: 'absolute',
    backgroundColor: theme.palette.common.black,
    opacity: 0.6,
    borderRadius: '8px',
  },
  'svg, .overlay': {
    visibility: 'hidden',
  },
  '&:hover svg, &:hover .overlay': {
    visibility: 'visible',
  },
}))

export const FileNameTypography = styled(Typography)({
  color: '#3B95FF',
  fontWeight: '700',
  fontSize: '13px',
  lineHeight: '22px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'wrap',
})

interface File extends Blob {
  name: string
  type: string
}
interface Accept {
  [key: string]: string[]
}

interface IFileUploadOutlinedProps {
  name: string
  label?: string
  type: string
  accept?: Accept
  showProgress?: boolean
  processing: boolean
  isError: boolean
  value: File
  progress?: number
  maxSize?: number
  maxLength?: number
  onDrop: Function
  onDelete: Function
  onFileDialogOpen?: VoidFunction
  onFileDialogCancel?: VoidFunction
  caption?: string
  sx?: SxProps
  disabled?: boolean
}

export default function FileUploadOutlined({
  name = 'file',
  label = 'key_upload_file',
  type,
  accept = { 'image/*': [] },
  value,
  showProgress,
  processing = false,
  isError,
  progress = 0,
  maxSize = 5242880,
  maxLength,
  onDrop,
  onDelete,
  onFileDialogOpen = () => {},
  onFileDialogCancel = () => {},
  caption,
  sx,
  disabled = false,
}: IFileUploadOutlinedProps) {
  const { t } = useLocales()
  const [file, setFile] = useState<File | null>(value || null)

  const handleDrop = useCallback((acceptedFiles: File[]) => {
    onFileDialogOpen()
    const file = acceptedFiles[0]
    if (file) {
      if (type === 'video') {
        const video = document.createElement('video')
        video.preload = 'metadata'
        video.onloadeddata = () => {
          window.URL.revokeObjectURL(video.src)
          if (maxLength && Math.floor(video.duration) > maxLength) {
            onDrop(file, true)
          } else {
            onDrop(file)
            setFile(file)
          }
        }
        video.src = URL.createObjectURL(file)
        video.load()
      } else {
        onDrop(file)
        setFile(file)
      }
    }
  }, [])

  useEffect(() => {
    setFile(value)
  }, [value])

  return (
    <>
      {label !== '' && (
        <Typography variant="subtitle2">{t(label)}</Typography>
      )}
      {!file && (
        <RHFUploadOutlined
          name={name}
          type={type}
          maxSize={maxSize}
          onDrop={handleDrop}
          accept={accept}
          sx={{
            marginTop: '14px !important',
            ...sx,
          }}
          onFileDialogOpen={onFileDialogOpen}
          onFileDialogCancel={onFileDialogCancel}
          caption={caption}
          disabled={disabled}
        />
      )}
      {file && (
        <>
          {showProgress && progress < 100 && (
            <>
              <ProgressBar
                label={file.name}
                progress={progress}
                isError={isError}
                type={type}
                processing={processing}
                onDelete={() => {
                  onDelete()
                  setFile(null)
                }}
                onRetry={() => {
                  handleDrop([file])
                }}
              />
              {isError && (
                <FormHelperText
                  error
                  sx={{
                    px: 2,
                    mt: '8px !important',
                    textAlign: 'left',
                  }}
                >
                  <>
                    <Iconify
                      icon="bxs:error-circle"
                      sx={{
                        marginBottom: '-2px',
                        marginRight: '5px',
                      }}
                    />
                    {t('key_upload_error')}
                  </>
                </FormHelperText>
              )}
            </>
          )}

          {(!showProgress || (showProgress && progress === 100)) && (
            <Box
              sx={{
                marginTop: [
                  'idCardPhoto',
                  'npwpPhoto',
                  'id_card_photo',
                  'selfie_photo',
                ].includes(name)
                  ? '0px'
                  : '14px',
              }}
            >
              {name === 'idCardPhoto' ||
              name === 'npwpPhoto' ||
              name === 'id_card_photo' ||
              name === 'selfie_photo' ? (
                <DropZoneIdCard>
                  <Preview file={file} type={type} />
                </DropZoneIdCard>
              ) : name === 'event_cover_file' ||
                name === 'event_teaser_file' ||
                name === 'event_venue_map_file' ? (
                <DropzoneEvent
                  file={file}
                  name={name}
                  onDelete={onDelete}
                  setFile={setFile}
                  disabled={disabled}
                >
                  {({ videoControl }) => (
                    <Preview
                      videoControl={videoControl}
                      file={file}
                      type={type}
                    />
                  )}
                </DropzoneEvent>
              ) : (
                <DropZoneStyle>
                  <Preview file={file} type={type} />
                </DropZoneStyle>
              )}
              {name !== 'event_cover_file' &&
                name !== 'event_teaser_file' &&
                name !== 'event_venue_map_file' && (
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ marginBottom: '16px' }}
                  >
                    <FileNameTypography>
                      {file.name}
                    </FileNameTypography>
                    {!disabled && (
                      <Iconify
                        id={`b-common-fileuploadoutlined-delete-${name}`}
                        icon="bxs:trash-alt"
                        color="#CB1C4F"
                        onClick={() => {
                          onDelete()
                          setFile(null)
                        }}
                        width={14}
                        height={14}
                        sx={{ minWidth: '14px', minHeight: '14px' }}
                      />
                    )}
                  </Stack>
                )}
            </Box>
          )}
        </>
      )}
    </>
  )
}

const Preview = memo(function Preview({
  videoControl = false,
  file,
  type,
}: {
  videoControl?: boolean
  file: File
  type: string
}) {
  if (!file) {
    return null
  }

  switch (type) {
    case 'image':
      return <img src={URL.createObjectURL(file)} />
    case 'video':
      return (
        <video
          controls={videoControl}
          src={URL.createObjectURL(file)}
          style={{ height: '100%', width: '100%' }}
        />
      )
  }
})

function DropzoneEvent({
  file,
  name,
  children,
  onDelete,
  setFile,
  disabled = false,
}) {
  const [isPreviewModalOpened, openPreviewModal] = useState(false)

  return (
    <DropZoneEventCard>
      <Stack
        flex={1}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <DropZoneEventSmallPreview
            id={`b-common-fileuploadoutlined-preview-${name}`}
            onClick={() => openPreviewModal(true)}
          >
            <Box className="overlay" />
            <Iconify
              icon="material-symbols:zoom-in"
              color="#fff"
              width={22}
              height={22}
              sx={{
                position: 'absolute',
                cursor: 'pointer',
                top: '30%',
                left: '30%',
              }}
            />
            {children({ videoControl: false })}
          </DropZoneEventSmallPreview>
          <Stack>
            <FileNameTypography>{file.name}</FileNameTypography>
            <Typography
              variant="body2"
              color={(theme) => theme.palette.grey[600]}
            >
              {formatBytes(file?.size)}
            </Typography>
          </Stack>
        </Stack>
        {!disabled && (
          <Iconify
            id={`b-common-fileuploadoutlined-dropzone-delete-${name}`}
            icon="bxs:trash-alt"
            color="#CB1C4F"
            onClick={() => {
              onDelete()
              setFile(null)
            }}
            width={14}
            height={14}
            sx={{
              minWidth: '14px',
              minHeight: '14px',
              cursor: 'pointer',
            }}
          />
        )}
      </Stack>
      <Dialog
        open={isPreviewModalOpened}
        onClose={() => openPreviewModal(false)}
      >
        {children({ videoControl: true })}
      </Dialog>
    </DropZoneEventCard>
  )
}
